import React, { useEffect, useState } from 'react';

import { CharacterResourceWithUsed, CharacterSheetInterface } from "@dndModels";

import FlexBox from '@components/layout/FlexBox';
import SelectStringBox from '@components/input/selects/SelectStringBox';
import { DICE_SIZE_OPTIONS, ONES_0_to_50 } from '@dndModels/Constants';

interface Props {
  char: CharacterSheetInterface;
}

/**
 * Overrides are keyed by the resource name (type_id), then the class_id of the resource.
 */
export default function CharacterResourceCustomizations({
  char,
}: Props) {
  const [reloading, setReloading] = useState(false);
  const [ping, setPing] = useState(false);

  const customizations = char.customizations;

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading]);

  const renderResourceCustomization = (resource_name: string, char_resource: CharacterResourceWithUsed | undefined): React.JSX.Element => {
    const class_id = char_resource ? char_resource.class_id : "";
    const display_name = char_resource ? char_resource.name : resource_name;
    const show_size = char_resource !== undefined &&
      ((char_resource.resource_feature?.default_dice_size ?? 1) !== 1 || char_resource.size > 1);
    const overrides = customizations.resource_overrides[resource_name];
    return (
      <FlexBox key={`${resource_name}_${class_id}`}>
        <FlexBox direction="row">
          <FlexBox xs_expand={0} style={{ width: "30%"}}>
            <FlexBox direction="column">
              <FlexBox>{ display_name }{ char_resource && char_resource.show_source_name && <> ({ char_resource.source_name })</>}</FlexBox>
              { char_resource &&
                <FlexBox className="small_label gray">Max: { char_resource.total }{ show_size && <>, Size: d{ char_resource.size }</>}</FlexBox>
              }
              { overrides && overrides[class_id] !== undefined &&
                <FlexBox className="small_label gray">Overridden</FlexBox>
              }
            </FlexBox>
          </FlexBox>
          <FlexBox style={{ width: "35%" }}>
            <SelectStringBox
              name="Override Max"
              options={["Don't Override",...ONES_0_to_50]}
              value={overrides === undefined || overrides[class_id] === undefined || overrides[class_id].count === undefined ? "Don't Override" : `${overrides[class_id].count}` }
              onChange={(changed: string) => {
                if (changed === "Don't Override") {
                  if (overrides && overrides[class_id]) {
                    if (overrides[class_id].count !== undefined) {
                      delete overrides[class_id].count;
                    } else {
                      delete overrides[class_id];
                    }
                    if (Object.keys(overrides).length === 0) {
                      delete customizations.resource_overrides[resource_name];
                    }
                  }
                } else {
                  if (customizations.resource_overrides[resource_name] === undefined) {
                    customizations.resource_overrides[resource_name] = {};
                  }
                  if (customizations.resource_overrides[resource_name][class_id] === undefined) {
                    customizations.resource_overrides[resource_name][class_id] = {};
                  }
                  customizations.resource_overrides[resource_name][class_id].count = +changed;
                }
                setPing(!ping);
                char.changed();
                char.subscription_holder.recalculate();
              }}
            />
          </FlexBox>
          <FlexBox style={{ width: "35%" }}>
            { show_size &&
              <SelectStringBox
                name="Override Size"
                options={DICE_SIZE_OPTIONS}
                extra_options={["Don't Override"]}
                value={overrides === undefined || overrides[class_id] === undefined || overrides[class_id].size === undefined ? "Don't Override" : `${overrides[class_id].size}` }
                onChange={(changed: string) => {
                  if (changed === "Don't Override") {
                    if (overrides && overrides[class_id]) {
                      if (overrides[class_id].size !== undefined) {
                        delete overrides[class_id].size;
                      } else {
                        delete overrides[class_id];
                      }
                      if (Object.keys(overrides).length === 0) {
                        delete customizations.resource_overrides[resource_name];
                      }
                    }
                  } else {
                    if (customizations.resource_overrides[resource_name] === undefined) {
                      customizations.resource_overrides[resource_name] = {};
                    }
                    if (customizations.resource_overrides[resource_name][class_id] === undefined) {
                      customizations.resource_overrides[resource_name][class_id] = {};
                    }
                    customizations.resource_overrides[resource_name][class_id].size = +changed;
                  }
                  setPing(!ping);
                  char.changed();
                  char.subscription_holder.recalculate();
                }}
              />
            }
          </FlexBox>
        </FlexBox>
      </FlexBox>
    );
  }

  const char_resources = char.body.resources
    .filter(o => !o.is_slot)
    .sort((a, b) => a.name.localeCompare(b.name));
  // Overrides for resources the character doesn't have (the ones that give it to them are already in char_resources)
  const other_names = Object.keys(customizations.resource_overrides)
    .filter(name => !char_resources.some(o => o.type_id === name))
    .sort((a, b) => a.localeCompare(b));

  return (
    <FlexBox direction="column" style={{ width: "100%" }}>
      { char_resources.map(cr => renderResourceCustomization(cr.type_id, cr)) }
      { other_names.map(name => renderResourceCustomization(name, undefined)) }
    </FlexBox>
  );
}
